import React, { useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import {
  IoGridOutline, IoPeopleOutline, IoCubeOutline,
  IoCartOutline, IoConstructOutline, IoCalendarOutline, IoMegaphoneOutline,
  IoRocketOutline, IoSettingsOutline, IoLogOutOutline,
} from 'react-icons/io5';

const NAV_ITEMS = [
  { path: '/admin', icon: IoGridOutline, label: '대시보드', end: true },
  { path: '/admin/users', icon: IoPeopleOutline, label: '회원 관리' },
  { path: '/admin/products', icon: IoCubeOutline, label: '상품 관리' },
  { path: '/admin/orders', icon: IoCartOutline, label: '주문 관리' },
  { path: '/admin/construction', icon: IoConstructOutline, label: '시공 관리' },
  { path: '/admin/schedule', icon: IoCalendarOutline, label: '일정 관리' },
  { path: '/admin/notice', icon: IoMegaphoneOutline, label: '공지 관리' },
  { path: '/admin/updates', icon: IoRocketOutline, label: '앱 업데이트' },
  { path: '/admin/settings', icon: IoSettingsOutline, label: '설정' },
];

const PAGE_TITLES = {
  '/admin': '대시보드',
  '/admin/users': '회원 관리',
  '/admin/products': '상품 관리',
  '/admin/orders': '주문 관리',
  '/admin/construction': '시공 관리',
  '/admin/schedule': '일정 관리',
  '/admin/notice': '공지 관리',
  '/admin/updates': '앱 업데이트',
  '/admin/settings': '설정',
};

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const pageTitle = PAGE_TITLES[location.pathname] || '관리자';

  const adminSession = (() => {
    try { return JSON.parse(localStorage.getItem('adminSession')); } catch { return null; }
  })();

  const handleLogout = () => {
    localStorage.removeItem('adminSession');
    navigate('/admin/login', { replace: true });
  };

  useEffect(() => {
    const root = document.getElementById('root');
    root?.setAttribute('data-admin', 'true');
    return () => root?.removeAttribute('data-admin');
  }, []);

  return (
    <Screen>
      <Frame>
        {/* 사이드바 */}
        <Sidebar>
          <LogoArea>
            <LogoText>조군산업</LogoText>
            <LogoSub>REXCRETE Admin</LogoSub>
          </LogoArea>

          <Nav>
            {NAV_ITEMS.map(({ path, icon: Icon, label, end }) => (
              <StyledNavLink key={path} to={path} end={end}>
                <Icon size={19} />
                <span>{label}</span>
              </StyledNavLink>
            ))}
          </Nav>

          <SidebarBottom>
            <LogoutBtn onClick={handleLogout}>
              <IoLogOutOutline size={19} />
              <span>로그인 화면으로 이동</span>
            </LogoutBtn>
          </SidebarBottom>
        </Sidebar>

        {/* 메인 영역 */}
        <RightArea>
          {/* 상단바 */}
          <Topbar>
            <TopTitle>{pageTitle}</TopTitle>
            <TopRight>
              <TopDate>{new Date().toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' })}</TopDate>
              <AdminBadge>{adminSession?.name || '관리자'}</AdminBadge>
            </TopRight>
          </Topbar>

          {/* 콘텐츠 */}
          <Content>
            <Outlet />
          </Content>
        </RightArea>
      </Frame>
    </Screen>
  );
};

export default AdminLayout;

/* 전체 화면 배경 */
const Screen = styled.div`
  height: 100vh;
  background: #2C3640;
  padding: 24px;
  display: flex;
  overflow: hidden;
`;

/* 둥근 프레임 카드 */
const Frame = styled.div`
  flex: 1;
  display: flex;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 40px rgba(0,0,0,0.3);
`;

const Sidebar = styled.aside`
  width: 220px;
  background: #FFFFFF;
  border-right: 1px solid #ECEEE9;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
`;

const LogoArea = styled.div`
  padding: 24px 20px 20px;
  border-bottom: 1px solid #ECEEE9;
`;

const LogoText = styled.div`
  font-size: 18px;
  font-weight: 500;
  color: #1E2A32;
  letter-spacing: -0.5px;
`;

const LogoSub = styled.div`
  font-size: 11px;
  color: #8C9AA4;
  margin-top: 3px;
  letter-spacing: 0.5px;
`;

const Nav = styled.nav`
  flex: 1;
  padding: 12px 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const StyledNavLink = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 10px 14px;
  border-radius: 10px;
  font-size: 14.5px;
  font-weight: 700;
  color: #8C9AA4;
  transition: all 0.15s;

  &:hover {
    background: #F5F5F0;
    color: #1E2A32;
  }

  &.active {
    background: #F0F0F0;
    color: #1E2A32;
    font-weight: 800;
  }
`;

const SidebarBottom = styled.div`
  padding: 10px 8px 16px;
  border-top: 1px solid #ECEEE9;
`;

const LogoutBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 10px 14px;
  border-radius: 10px;
  font-size: 13.5px;
  font-weight: 600;
  color: #8C9AA4;
  width: 100%;
  transition: all 0.15s;

  &:hover {
    background: #F5F5F0;
    color: #1E2A32;
  }
`;

const RightArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #F5F5F0;
  min-height: 0;
`;

const Topbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 28px;
  background: #FFFFFF;
  border-bottom: 1px solid #ECEEE9;
  flex-shrink: 0;
`;

const TopTitle = styled.h2`
  font-size: 17px;
  font-weight: 500;
  color: #1E2A32;
`;

const TopRight = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const TopDate = styled.span`
  font-size: 12.5px;
  color: #8C9AA4;
`;

const AdminBadge = styled.span`
  font-size: 11px;
  padding: 4px 12px;
  border-radius: 20px;
  background: #F0F0F0;
  color: #1E2A32;
  font-weight: 400;
`;

const Content = styled.main`
  flex: 1;
  overflow-y: auto;
`;
